import { z } from "zod";

const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Use a six-digit HEX color, for example #0a0a0a.");
const gradientAngle = z.enum(["90deg", "120deg", "135deg", "160deg", "180deg"]);
const engagementLayout = z.enum(["editorial", "cards", "split"]);
const engagementRadius = z.enum(["0px", "12px", "20px", "28px"]);
const engagementGap = z.enum(["0px", "16px", "24px", "32px"]);
const engagementSpacing = z.enum(["compact", "standard", "spacious"]);

export const THEME_COLOR_KEYS = [
  "foreground",
  "muted_foreground",
  "border",
  "primary_action",
  "primary_action_text",
  "link_accent",
  "platform_accent",
  "growth_accent",
  "seo_accent",
  "systems_accent",
  "data_accent",
  "editorial_accent",
  "dark_rail",
  "dark_accent",
  "surface_cool",
  "surface_warm",
  "press_plate",
  "home_border",
  "home_hero_from",
  "home_hero_to",
  "home_situations_from",
  "home_situations_to",
  "home_work_from",
  "home_work_to",
  "home_engagement_from",
  "home_engagement_to",
  "home_engagement_agency_bg",
  "home_engagement_agency_text",
  "home_engagement_agency_border",
  "home_engagement_tasker_bg",
  "home_engagement_tasker_text",
  "home_engagement_tasker_border",
  "home_faq_from",
  "home_faq_to",
  "services_hero_from",
  "services_hero_to",
  "services_situations_from",
  "services_situations_to",
  "services_atlas_from",
  "services_atlas_to",
  "services_chapters_from",
  "services_chapters_to",
] as const;

export const THEME_ANGLE_KEYS = ["home_gradient_angle", "services_gradient_angle"] as const;

export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];
export type ThemeAngleKey = (typeof THEME_ANGLE_KEYS)[number];
export type ThemeGradientAngle = z.infer<typeof gradientAngle>;
export type ThemeEngagementLayout = z.infer<typeof engagementLayout>;
export type ThemeEngagementRadius = z.infer<typeof engagementRadius>;
export type ThemeEngagementGap = z.infer<typeof engagementGap>;
export type ThemeEngagementSpacing = z.infer<typeof engagementSpacing>;

export type ThemeColors = Record<ThemeColorKey, string> &
  Record<ThemeAngleKey, ThemeGradientAngle> & {
    home_engagement_layout: ThemeEngagementLayout;
    home_engagement_radius: ThemeEngagementRadius;
    home_engagement_gap: ThemeEngagementGap;
    home_engagement_spacing: ThemeEngagementSpacing;
    home_engagement_section_border: boolean;
    home_engagement_card_borders: boolean;
  };

export const DEFAULT_THEME_COLORS: ThemeColors = {
  // Official Shugert identity: Jet, Charcoal, Off White, Mist, Signal Lime,
  // Sage and Mid Gray. Signal Lime is reserved for primary actions and small
  // proof/status markers only.
  foreground: "#0A0A0A",
  // Mid Gray darkened to #5F6462 so secondary copy clears WCAG AA (>=4.5:1)
  // on every light surface: Off White (5.76), Mist (5.37) and Sage (5.06).
  muted_foreground: "#5F6462",
  border: "#DDDEDC",
  primary_action: "#CCFF00",
  primary_action_text: "#0A0A0A",
  link_accent: "#0A0A0A",
  platform_accent: "#0A0A0A",
  // Low-chroma capability accents. They read as neutrals with character and
  // never compete with Signal Lime, which stays reserved for primary actions.
  growth_accent: "#3F5647",
  seo_accent: "#3A4A57",
  systems_accent: "#6B5335",
  data_accent: "#4A4356",
  editorial_accent: "#6B5A3C",
  dark_rail: "#0A0A0A",
  dark_accent: "#CCFF00",
  surface_cool: "#F2F2F0",
  surface_warm: "#E6EDE6",
  press_plate: "#FAFAF8",
  home_border: "#DDDEDC",
  home_hero_from: "#FAFAF8",
  home_hero_to: "#F2F2F0",
  home_situations_from: "#FAFAF8",
  home_situations_to: "#F2F2F0",
  home_work_from: "#F2F2F0",
  home_work_to: "#FAFAF8",
  home_engagement_from: "#FAFAF8",
  home_engagement_to: "#F2F2F0",
  home_engagement_agency_bg: "#0A0A0A",
  home_engagement_agency_text: "#FAFAF8",
  home_engagement_agency_border: "#2A2A2A",
  home_engagement_tasker_bg: "#1F1F1F",
  home_engagement_tasker_text: "#FAFAF8",
  home_engagement_tasker_border: "#2A2A2A",
  home_faq_from: "#FAFAF8",
  home_faq_to: "#F2F2F0",
  services_hero_from: "#FAFAF8",
  services_hero_to: "#F2F2F0",
  services_situations_from: "#FAFAF8",
  services_situations_to: "#F2F2F0",
  services_atlas_from: "#FAFAF8",
  services_atlas_to: "#F2F2F0",
  services_chapters_from: "#F2F2F0",
  services_chapters_to: "#FAFAF8",
  home_gradient_angle: "135deg",
  services_gradient_angle: "135deg",
  home_engagement_layout: "editorial",
  home_engagement_radius: "20px",
  home_engagement_gap: "24px",
  home_engagement_spacing: "standard",
  home_engagement_section_border: true,
  home_engagement_card_borders: true,
};

export const ThemeColorsSchema = z.object({
  ...(Object.fromEntries(THEME_COLOR_KEYS.map((key) => [key, hexColor])) as Record<ThemeColorKey, typeof hexColor>),
  home_gradient_angle: gradientAngle,
  services_gradient_angle: gradientAngle,
  home_engagement_layout: engagementLayout,
  home_engagement_radius: engagementRadius,
  home_engagement_gap: engagementGap,
  home_engagement_spacing: engagementSpacing,
  home_engagement_section_border: z.boolean(),
  home_engagement_card_borders: z.boolean(),
});

export function normalizeThemeColors(input: unknown): ThemeColors {
  const candidate = input && typeof input === "object"
    ? { ...DEFAULT_THEME_COLORS, ...(input as Partial<ThemeColors>) }
    : DEFAULT_THEME_COLORS;
  const result = ThemeColorsSchema.safeParse(candidate);
  return result.success ? result.data : DEFAULT_THEME_COLORS;
}

function buildEngagementStyles(colors: ThemeColors) {
  const sectionBorderWidth = colors.home_engagement_section_border ? "1px" : "0px";
  const cardBorderWidth = colors.home_engagement_card_borders ? "1px" : "0px";
  const padding = colors.home_engagement_spacing === "compact"
    ? "4rem"
    : colors.home_engagement_spacing === "spacious"
      ? "8rem"
      : "6rem";

  const shared = `
.home-money-palette > section:nth-of-type(10) {
  background-color: var(--theme-home-engagement-from) !important;
  background-image: linear-gradient(var(--theme-home-gradient-angle), var(--theme-home-engagement-from), var(--theme-home-engagement-to)) !important;
  border-top-width: ${sectionBorderWidth} !important;
  border-bottom-width: ${sectionBorderWidth} !important;
  border-color: var(--theme-home-line) !important;
  padding-top: ${padding} !important;
  padding-bottom: ${padding} !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child {
  border-color: var(--theme-home-line) !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a {
  border-color: var(--theme-home-line) !important;
}
`;

  if (colors.home_engagement_layout === "editorial") {
    return `${shared}
.home-money-palette > section:nth-of-type(10) > div > div:last-child {
  border-top-width: ${cardBorderWidth} !important;
  border-bottom-width: ${cardBorderWidth} !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a {
  border-bottom-width: ${cardBorderWidth} !important;
}
`;
  }

  const split = colors.home_engagement_layout === "split";
  const gap = split ? "0px" : colors.home_engagement_gap;
  const radius = colors.home_engagement_radius;

  return `${shared}
.home-money-palette > section:nth-of-type(10) > div {
  grid-template-columns: minmax(0, 1fr) !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:first-child,
.home-money-palette > section:nth-of-type(10) > div > div:last-child {
  grid-column: 1 / -1 !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:first-child {
  max-width: 56rem;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: ${gap} !important;
  border-width: 0 !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a {
  min-height: 15rem;
  align-content: space-between;
  padding: 2rem !important;
  border-style: solid !important;
  border-width: ${cardBorderWidth} !important;
  border-radius: ${split ? "0px" : radius} !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:first-child {
  background: var(--theme-home-engagement-agency-bg) !important;
  border-color: var(--theme-home-engagement-agency-border) !important;
  ${split ? `border-radius: ${radius} 0 0 ${radius} !important;` : ""}
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:last-child {
  background: var(--theme-home-engagement-tasker-bg) !important;
  border-color: var(--theme-home-engagement-tasker-border) !important;
  ${split ? `border-radius: 0 ${radius} ${radius} 0 !important;` : ""}
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:first-child h3,
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:first-child p,
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:first-child svg {
  color: var(--theme-home-engagement-agency-text) !important;
}
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:last-child h3,
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:last-child p,
.home-money-palette > section:nth-of-type(10) > div > div:last-child > a:last-child svg {
  color: var(--theme-home-engagement-tasker-text) !important;
}
@media (max-width: 767px) {
  .home-money-palette > section:nth-of-type(10) > div > div:last-child {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: ${colors.home_engagement_gap} !important;
  }
  .home-money-palette > section:nth-of-type(10) > div > div:last-child > a:first-child,
  .home-money-palette > section:nth-of-type(10) > div > div:last-child > a:last-child {
    border-radius: ${radius} !important;
  }
}
`;
}

export function buildThemeStylesheet(input: unknown): string {
  const colors = normalizeThemeColors(input);
  return `/* Generated from Admin → Theme. */
:root {
  --theme-foreground: ${colors.foreground};
  --theme-muted-foreground: ${colors.muted_foreground};
  --theme-border: ${colors.border};
  --theme-primary-action: ${colors.primary_action};
  --theme-primary-action-text: ${colors.primary_action_text};
  --theme-link-accent: ${colors.link_accent};
  --theme-platform-accent: ${colors.platform_accent};
  --theme-growth-accent: ${colors.growth_accent};
  --theme-seo-accent: ${colors.seo_accent};
  --theme-systems-accent: ${colors.systems_accent};
  --theme-data-accent: ${colors.data_accent};
  --theme-editorial-accent: ${colors.editorial_accent};
  --theme-dark-rail: ${colors.dark_rail};
  --theme-dark-accent: ${colors.dark_accent};
  --theme-surface-cool: ${colors.surface_cool};
  --theme-surface-warm: ${colors.surface_warm};
  --theme-press-plate: ${colors.press_plate};
  --theme-home-line: ${colors.home_border};

  --theme-home-gradient-angle: ${colors.home_gradient_angle};
  --theme-services-gradient-angle: ${colors.services_gradient_angle};
  --theme-home-hero-from: ${colors.home_hero_from};
  --theme-home-hero-to: ${colors.home_hero_to};
  --theme-home-situations-from: ${colors.home_situations_from};
  --theme-home-situations-to: ${colors.home_situations_to};
  --theme-home-work-from: ${colors.home_work_from};
  --theme-home-work-to: ${colors.home_work_to};
  --theme-home-engagement-from: ${colors.home_engagement_from};
  --theme-home-engagement-to: ${colors.home_engagement_to};
  --theme-home-engagement-agency-bg: ${colors.home_engagement_agency_bg};
  --theme-home-engagement-agency-text: ${colors.home_engagement_agency_text};
  --theme-home-engagement-agency-border: ${colors.home_engagement_agency_border};
  --theme-home-engagement-tasker-bg: ${colors.home_engagement_tasker_bg};
  --theme-home-engagement-tasker-text: ${colors.home_engagement_tasker_text};
  --theme-home-engagement-tasker-border: ${colors.home_engagement_tasker_border};
  --theme-home-faq-from: ${colors.home_faq_from};
  --theme-home-faq-to: ${colors.home_faq_to};
  --theme-services-hero-from: ${colors.services_hero_from};
  --theme-services-hero-to: ${colors.services_hero_to};
  --theme-services-situations-from: ${colors.services_situations_from};
  --theme-services-situations-to: ${colors.services_situations_to};
  --theme-services-atlas-from: ${colors.services_atlas_from};
  --theme-services-atlas-to: ${colors.services_atlas_to};
  --theme-services-chapters-from: ${colors.services_chapters_from};
  --theme-services-chapters-to: ${colors.services_chapters_to};

  --theme-platform-soft: color-mix(in srgb, var(--theme-platform-accent) 5.5%, transparent);
  --theme-platform-line: color-mix(in srgb, var(--theme-platform-accent) 20%, transparent);
  --theme-growth-soft: color-mix(in srgb, var(--theme-growth-accent) 5.5%, transparent);
  --theme-growth-line: color-mix(in srgb, var(--theme-growth-accent) 20%, transparent);
  --theme-seo-soft: color-mix(in srgb, var(--theme-seo-accent) 5.5%, transparent);
  --theme-seo-line: color-mix(in srgb, var(--theme-seo-accent) 20%, transparent);
  --theme-systems-soft: color-mix(in srgb, var(--theme-systems-accent) 5.5%, transparent);
  --theme-systems-line: color-mix(in srgb, var(--theme-systems-accent) 20%, transparent);
  --theme-data-soft: color-mix(in srgb, var(--theme-data-accent) 5.5%, transparent);
  --theme-data-line: color-mix(in srgb, var(--theme-data-accent) 20%, transparent);
  --theme-editorial-soft: color-mix(in srgb, var(--theme-editorial-accent) 5%, transparent);
  --theme-editorial-line: color-mix(in srgb, var(--theme-editorial-accent) 22%, transparent);
  /* Dividers on dark rails stay Off White at low opacity so Signal Lime is
   * never spent on structural lines. */
  --theme-dark-line: rgb(250 250 248 / 14%);

  --background: ${colors.home_hero_from};
  --card: ${colors.press_plate};
  --popover: ${colors.press_plate};
  --foreground: var(--theme-foreground);
  --card-foreground: var(--theme-foreground);
  --popover-foreground: var(--theme-foreground);
  --muted-foreground: var(--theme-muted-foreground);
  --border: var(--theme-border);
  --input: var(--theme-border);
  --primary: var(--theme-primary-action);
  --primary-foreground: var(--theme-primary-action-text);
  --ring: var(--theme-link-accent);
}

.home-money-palette,
.services-money-palette {
  --hub-ink: var(--theme-foreground);
  --hub-muted: var(--theme-muted-foreground);
  --hub-line: var(--theme-border);
  --hub-navy: var(--theme-dark-rail);
  --hub-bronze: var(--theme-editorial-accent);
  --hub-bronze-strong: var(--theme-editorial-accent);
  --hub-bronze-soft: var(--theme-surface-warm);
  --hub-bronze-line: var(--theme-editorial-line);
  --hub-platform: var(--theme-platform-accent);
  --hub-platform-soft: var(--theme-platform-soft);
  --hub-platform-line: var(--theme-platform-line);
  --hub-violet: var(--theme-dark-accent);
  --hub-violet-line: var(--theme-dark-line);
  --hub-neutral-soft: var(--theme-surface-cool);
}

.home-money-palette a[class*="bg-blue-700"],
.services-money-palette a[class*="bg-blue-700"] {
  background-color: var(--theme-primary-action) !important;
  color: var(--theme-primary-action-text) !important;
}
.home-money-palette > section:nth-of-type(4) img {
  background: transparent !important;
}

/* Named page surfaces. Equal start/end colors produce a solid background. */
/* The hero owns its own institutional dark surface. */
.home-money-palette > section:nth-of-type(1):not(.site-home-hero) {
  background-color: var(--theme-home-hero-from) !important;
  background-image: linear-gradient(var(--theme-home-gradient-angle), var(--theme-home-hero-from), var(--theme-home-hero-to)) !important;
}
.home-money-palette > section:nth-of-type(5) {
  background-color: var(--theme-home-situations-from) !important;
  background-image: linear-gradient(var(--theme-home-gradient-angle), var(--theme-home-situations-from), var(--theme-home-situations-to)) !important;
}
/* The selected-work chapter owns its own institutional dark surface. */
.home-money-palette > section:nth-of-type(7):not(.site-home-work) {
  background-color: var(--theme-home-work-from) !important;
  background-image: linear-gradient(var(--theme-home-gradient-angle), var(--theme-home-work-from), var(--theme-home-work-to)) !important;
}
.home-money-palette > section:nth-of-type(11) {
  background-color: var(--theme-home-faq-from) !important;
  background-image: linear-gradient(var(--theme-home-gradient-angle), var(--theme-home-faq-from), var(--theme-home-faq-to)) !important;
}
.services-money-palette > section:nth-of-type(1) {
  background-color: var(--theme-services-hero-from) !important;
  background-image: linear-gradient(var(--theme-services-gradient-angle), var(--theme-services-hero-from), var(--theme-services-hero-to)) !important;
}
.services-money-palette > section:nth-of-type(2) {
  background-color: var(--theme-services-situations-from) !important;
  background-image: linear-gradient(var(--theme-services-gradient-angle), var(--theme-services-situations-from), var(--theme-services-situations-to)) !important;
}
.services-money-palette > section:nth-of-type(3) {
  background-color: var(--theme-services-atlas-from) !important;
  background-image: linear-gradient(var(--theme-services-gradient-angle), var(--theme-services-atlas-from), var(--theme-services-atlas-to)) !important;
}
.services-money-palette > section:nth-of-type(4) {
  background-color: var(--theme-services-chapters-from) !important;
  background-image: linear-gradient(var(--theme-services-gradient-angle), var(--theme-services-chapters-from), var(--theme-services-chapters-to)) !important;
}

/* Home's formerly blue dividers now have one explicit page-level control. */
.home-money-palette > section:not(:nth-of-type(9)):not(:nth-of-type(10)) [class*="border-blue-"] {
  border-color: var(--theme-home-line) !important;
}

[data-page-main][style*="--money-accent: #0a0a0a"], [data-page-main][style*="--money-accent:#0a0a0a"] {
  --money-accent: var(--theme-platform-accent) !important;
  --money-soft: var(--theme-platform-soft) !important;
  --money-line: var(--theme-platform-line) !important;
  --money-strong: color-mix(in srgb, var(--theme-platform-accent) 78%, black) !important;
}
[data-page-main][style*="--money-accent: #1f1f1f"], [data-page-main][style*="--money-accent:#1f1f1f"] {
  --money-accent: var(--theme-growth-accent) !important;
  --money-soft: var(--theme-growth-soft) !important;
  --money-line: var(--theme-growth-line) !important;
  --money-strong: color-mix(in srgb, var(--theme-growth-accent) 78%, black) !important;
}
[data-page-main][style*="--money-accent: #1f1f1f"], [data-page-main][style*="--money-accent:#1f1f1f"] {
  --money-accent: var(--theme-systems-accent) !important;
  --money-soft: var(--theme-systems-soft) !important;
  --money-line: var(--theme-systems-line) !important;
  --money-strong: color-mix(in srgb, var(--theme-systems-accent) 78%, black) !important;
}
[data-page-main][style*="--money-accent: #1f1f1f"], [data-page-main][style*="--money-accent:#1f1f1f"] {
  --money-accent: var(--theme-data-accent) !important;
  --money-soft: var(--theme-data-soft) !important;
  --money-line: var(--theme-data-line) !important;
  --money-strong: color-mix(in srgb, var(--theme-data-accent) 78%, black) !important;
}
[data-page-main][style*="--technical-accent: #1f1f1f"], [data-page-main][style*="--technical-accent:#1f1f1f"] {
  --technical-accent: var(--theme-growth-accent) !important;
  --technical-soft: var(--theme-growth-soft) !important;
  --technical-line: var(--theme-growth-line) !important;
}
[data-page-main][style*="--technical-accent: #1f1f1f"], [data-page-main][style*="--technical-accent:#1f1f1f"] {
  --technical-accent: var(--theme-seo-accent) !important;
  --technical-soft: var(--theme-seo-soft) !important;
  --technical-line: var(--theme-seo-line) !important;
}

[class~="text-[#1f1f1f]"] { color: var(--theme-editorial-accent) !important; }
[class~="text-[#ccff00]"] { color: var(--theme-dark-accent) !important; }
[class~="bg-[#0a0a0a]"] { background-color: var(--theme-dark-rail) !important; }
[class~="border-[rgba(36,57,112,0.18)]"] { border-color: var(--theme-platform-line) !important; }
[class~="bg-[rgba(36,57,112,0.045)]"] { background-color: var(--theme-platform-soft) !important; }
[class~="border-[rgba(161,107,31,0.20)]"],
[class~="border-[rgba(161,107,31,0.24)]"],
[class~="border-[rgba(161,107,31,0.28)]"] { border-color: var(--theme-editorial-line) !important; }
[class~="bg-[rgba(161,107,31,0.045)]"] { background-color: var(--theme-editorial-soft) !important; }

${buildEngagementStyles(colors)}
`;
}

export const THEME_COLOR_FIELDS: ReadonlyArray<{
  key: ThemeColorKey;
  group: "Foundation" | "Actions" | "Service accents" | "Editorial surfaces";
  label: string;
  description: string;
  usedBy: string;
}> = [
  { key: "foreground", group: "Foundation", label: "Primary text", description: "Headings, body emphasis and neutral links.", usedBy: "All public pages" },
  { key: "muted_foreground", group: "Foundation", label: "Secondary text", description: "Supporting copy, labels and captions.", usedBy: "All public pages" },
  { key: "border", group: "Foundation", label: "Neutral borders", description: "Default hairlines, dividers and input borders.", usedBy: "Neutral sections and money-page rows" },
  { key: "primary_action", group: "Actions", label: "Primary action", description: "Background for primary CTA buttons.", usedBy: "Quote and scope buttons" },
  { key: "primary_action_text", group: "Actions", label: "Primary action text", description: "Text and icon color inside primary CTAs.", usedBy: "Primary CTA labels" },
  { key: "link_accent", group: "Actions", label: "Selective link accent", description: "Reserved hover and strategic inline-link color.", usedBy: "Editorial link hover and focus ring" },
  { key: "platform_accent", group: "Service accents", label: "Platform / development", description: "Development, apps and platform engineering.", usedBy: "Platform money pages and Shopify Plus panel lines" },
  { key: "growth_accent", group: "Service accents", label: "Performance / conversion", description: "Performance, CRO and customer-journey evidence.", usedBy: "Performance and CRO money pages" },
  { key: "seo_accent", group: "Service accents", label: "SEO / visibility", description: "Technical SEO, search and visibility signals.", usedBy: "Technical SEO money pages" },
  { key: "systems_accent", group: "Service accents", label: "Systems / integrations", description: "Integrations, data contracts and operations.", usedBy: "Integration and operations money pages" },
  { key: "data_accent", group: "Service accents", label: "Analytics / data", description: "Analytics, applied AI and measurement.", usedBy: "Analytics and applied AI money pages" },
  { key: "editorial_accent", group: "Editorial surfaces", label: "Editorial bronze", description: "Eyebrows, small numbers and restrained evidence details.", usedBy: "Home, Services and Shopify Plus details" },
  { key: "dark_rail", group: "Editorial surfaces", label: "Dark rail background", description: "Enterprise evidence and operating-model sections.", usedBy: "Shopify Plus, Home process and Services PULSE" },
  { key: "dark_accent", group: "Editorial surfaces", label: "Dark rail accent", description: "Labels, numbers and dividers on dark sections.", usedBy: "Dark enterprise rails" },
  { key: "surface_cool", group: "Editorial surfaces", label: "Cool surface", description: "Very light neutral/cool section background.", usedBy: "Evidence, work and selected editorial sections" },
  { key: "surface_warm", group: "Editorial surfaces", label: "Warm surface", description: "Subtle warm separation between adjacent sections.", usedBy: "Situation selectors and decision sections" },
  { key: "press_plate", group: "Editorial surfaces", label: "Press logo plate", description: "Background behind logos with white or transparent artwork.", usedBy: "Homepage press logos" },
];

export const THEME_BACKGROUND_FIELDS: ReadonlyArray<{
  label: string;
  description: string;
  usedBy: string;
  from: ThemeColorKey;
  to: ThemeColorKey;
  page: "Home" | "Services";
  section: number;
}> = [
  { label: "Hero", description: "Opening proposition and engagement brief.", usedBy: "Home → Hero", from: "home_hero_from", to: "home_hero_to", page: "Home", section: 1 },
  { label: "Recognizable situations", description: "Customer problems linked to canonical money pages.", usedBy: "Home → Recognizable situations", from: "home_situations_from", to: "home_situations_to", page: "Home", section: 5 },
  { label: "Selected work", description: "Evidence surface behind selected projects.", usedBy: "Home → Selected work", from: "home_work_from", to: "home_work_to", page: "Home", section: 7 },
  { label: "Engagement models", description: "Agency and Tasker Army decision section.", usedBy: "Home → Two engagement models", from: "home_engagement_from", to: "home_engagement_to", page: "Home", section: 10 },
  { label: "FAQ", description: "Questions before the first scope.", usedBy: "Home → FAQ", from: "home_faq_from", to: "home_faq_to", page: "Home", section: 11 },
  { label: "Hero", description: "Opening services proposition.", usedBy: "Services → Hero", from: "services_hero_from", to: "services_hero_to", page: "Services", section: 1 },
  { label: "Situation selector", description: "Situation-first service navigation.", usedBy: "Services → Situation selector", from: "services_situations_from", to: "services_situations_to", page: "Services", section: 2 },
  { label: "Decision map", description: "Surface around the four-domain decision map.", usedBy: "Services → Decision map", from: "services_atlas_from", to: "services_atlas_to", page: "Services", section: 3 },
  { label: "Service chapters", description: "Long editorial surface behind service chapters.", usedBy: "Services → Service chapters", from: "services_chapters_from", to: "services_chapters_to", page: "Services", section: 4 },
];

export const THEME_GRADIENT_ANGLES: ReadonlyArray<{ value: ThemeGradientAngle; label: string }> = [
  { value: "90deg", label: "Left to right" },
  { value: "120deg", label: "Diagonal · gentle" },
  { value: "135deg", label: "Diagonal · balanced" },
  { value: "160deg", label: "Diagonal · vertical" },
  { value: "180deg", label: "Top to bottom" },
];

export const THEME_ENGAGEMENT_LAYOUTS: ReadonlyArray<{ value: ThemeEngagementLayout; label: string; description: string }> = [
  { value: "editorial", label: "Editorial rows", description: "Current compact decision rail beside the section introduction." },
  { value: "cards", label: "Two cards", description: "Heading above two independent cards with configurable gap and corners." },
  { value: "split", label: "Split panel", description: "Heading above one seamless two-column panel." },
];

export const THEME_ENGAGEMENT_RADII: ReadonlyArray<{ value: ThemeEngagementRadius; label: string }> = [
  { value: "0px", label: "Square" },
  { value: "12px", label: "Small" },
  { value: "20px", label: "Medium" },
  { value: "28px", label: "Large" },
];

export const THEME_ENGAGEMENT_GAPS: ReadonlyArray<{ value: ThemeEngagementGap; label: string }> = [
  { value: "0px", label: "No gap" },
  { value: "16px", label: "Compact" },
  { value: "24px", label: "Standard" },
  { value: "32px", label: "Wide" },
];

export const THEME_ENGAGEMENT_SPACING: ReadonlyArray<{ value: ThemeEngagementSpacing; label: string }> = [
  { value: "compact", label: "Compact" },
  { value: "standard", label: "Standard" },
  { value: "spacious", label: "Spacious" },
];

export const THEME_PRESETS: ReadonlyArray<{ name: string; description: string; colors: ThemeColors }> = [
  { name: "Shugert Global (official)", description: "Official identity shared with shugert.es and shugertmarketing.com: Jet, Charcoal, Off White, Mist, Sage, Mid Gray and Signal Lime.", colors: DEFAULT_THEME_COLORS },
  {
    name: "Shugert Global · authority",
    description: "Same official palette with heavier Jet surfaces for the institutional Mexico expression.",
    colors: {
      ...DEFAULT_THEME_COLORS,
      home_work_from: "#0A0A0A",
      home_work_to: "#1F1F1F",
      surface_cool: "#E6EDE6",
    },
  },
  {
    name: "Shugert Global \u00b7 quiet",
    description: "Official palette with the softest neutral separation. Signal Lime remains the only accent.",
    colors: {
      ...DEFAULT_THEME_COLORS,
      surface_cool: "#F7F7F5",
      surface_warm: "#F2F2F0",
      home_hero_to: "#FAFAF8",
      home_situations_to: "#FAFAF8",
      services_hero_to: "#FAFAF8",
    },
  },
];
